import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, Maximize, Minimize } from "lucide-react";
import { useLang } from "@/lib/i18n";

export const ProjectLightbox = ({ project, media, startIndex = 0, onClose }) => {
  const { t } = useLang();
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(null);
  const [isFull, setIsFull] = useState(false);
  const rootRef = useRef(null);
  const touchX = useRef(null);
  const item = media[index];
  const total = media.length;
  const canFullscreen = typeof document !== "undefined" && !!document.documentElement.requestFullscreen;
  const isTouch = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

  const go = (step) => {
    setZoom(null);
    setIndex((i) => (i + step + total) % total);
  };

  const close = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !document.fullscreenElement) onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
    };
  });

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else rootRef.current.requestFullscreen();
  };

  const pointFrom = (e) => {
    const r = e.currentTarget.parentElement.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 };
  };

  const toggleZoom = (e) => setZoom(zoom ? null : pointFrom(e));

  const onTouchEnd = (e) => {
    if (zoom || touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const thumbsH = total > 1 ? "bottom-16" : "bottom-0";
  const navBtn = "absolute top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 hover:bg-emerald-600 text-white flex items-center justify-center transition-colors z-10";

  return (
    <div ref={rootRef} className="fixed inset-0 bg-black z-[60]" data-testid="lightbox">
      <div
        className={`absolute inset-x-0 top-0 ${thumbsH} flex items-center justify-center`}
        onClick={(e) => { if (zoom && e.target === e.currentTarget) setZoom(null); }}
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={onTouchEnd}
      >
        {item.type === "video" ? (
          <video key={item.src} controls autoPlay playsInline poster={item.poster} className="w-full h-full object-contain" data-testid="lightbox-video">
            <source src={item.src} type="video/mp4" />
          </video>
        ) : (
          <div className="relative w-full h-full overflow-hidden flex items-center justify-center">
            <img
              key={item.image}
              src={item.image}
              alt={`${t(project.title)} ${index + 1}`}
              onClick={toggleZoom}
              onMouseMove={(e) => zoom && !isTouch && setZoom(pointFrom(e))}
              className={`max-w-full max-h-full w-auto h-full object-contain transition-transform duration-300 ${zoom ? "cursor-zoom-out" : "cursor-zoom-in"}`}
              style={zoom ? { transform: "scale(2.5)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : undefined}
              draggable="false"
              data-testid="lightbox-image"
            />
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2.5 py-1.5 rounded-full flex items-center gap-1.5 pointer-events-none whitespace-nowrap" data-testid="lightbox-zoom-hint">
              {zoom ? <ZoomOut className="w-3.5 h-3.5" /> : <ZoomIn className="w-3.5 h-3.5" />}
              {zoom
                ? (isTouch ? t("Tap to zoom out", "Toque para alejar") : t("Move to explore · click to zoom out", "Mueva para explorar · clic para alejar"))
                : (isTouch ? t("Tap to zoom", "Toque para acercar") : t("Click to zoom", "Clic para acercar"))}
            </span>
          </div>
        )}
        {total > 1 && (
          <>
            <button onClick={() => go(-1)} className={`${navBtn} left-2 md:left-6`} aria-label={t("Previous", "Anterior")} data-testid="lightbox-prev">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button onClick={() => go(1)} className={`${navBtn} right-2 md:right-6`} aria-label={t("Next", "Siguiente")} data-testid="lightbox-next">
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent px-4 md:px-8 pt-3 pb-8 text-white pointer-events-none">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 max-w-3xl">
            <h3 className="font-bold text-base md:text-xl font-['Archivo'] truncate" data-testid="lightbox-project-title">{t(project.title)}</h3>
            <p className="text-slate-300 text-xs md:text-sm line-clamp-1 md:line-clamp-2" data-testid="lightbox-project-description">{t(project.description)}</p>
            <p className="text-slate-400 text-xs font-mono tracking-wider mt-1" data-testid="lightbox-counter">{index + 1} / {total}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0 pointer-events-auto">
            {canFullscreen && (
              <button className="text-white hover:text-emerald-400 transition-colors" onClick={toggleFullscreen} aria-label={t("Full screen", "Pantalla completa")} data-testid="lightbox-fullscreen">
                {isFull ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
              </button>
            )}
            <button className="text-white hover:text-emerald-400 transition-colors" onClick={close} aria-label={t("Close", "Cerrar")} data-testid="lightbox-close">
              <X className="w-8 h-8" />
            </button>
          </div>
        </div>
      </div>

      {total > 1 && (
        <div className="absolute inset-x-0 bottom-0 h-16 flex items-center gap-2 overflow-x-auto px-4 justify-start md:justify-center" data-testid="lightbox-thumbnails">
          {media.map((m, i) => (
            <button
              key={m.id}
              onClick={() => { setZoom(null); setIndex(i); }}
              className={`relative shrink-0 w-12 h-12 overflow-hidden border-2 transition-colors ${i === index ? "border-emerald-500" : "border-transparent opacity-50 hover:opacity-100"}`}
              data-testid={`lightbox-thumb-${i}`}
            >
              <img src={m.type === "video" ? m.poster : m.image} alt="" className="w-full h-full object-cover" />
              {m.type === "video" && <span className="absolute inset-0 flex items-center justify-center text-white text-[9px] font-bold bg-black/40">VIDEO</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
